// Red alert: School Manager's plan to catch up on everything missed (Everything → catchup), and the "Did you do
// these?" card that asks about every class and study session that has ended without a done / not done.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatchupSummary, DashboardItem, Everything, MarkItem } from '@tempo/core';
import { AlertTriangle, Check, CheckCircle2, Circle, Play, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { dayName, minutes, relative, time } from '../lib/format';
import { useNow } from '../lib/hooks';
import { navigate } from '../lib/router';
import { toast } from '../lib/toast';

export const useEverythingData = () => useQuery({ queryKey: ['everything'], queryFn: () => api.get<Everything>('/everything'), refetchInterval: 60_000 });

const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);

function hoursLeft(until: number, now: number) {
  const h = Math.max(0, (until - now) / 3_600_000);
  return h >= 24 ? `${Math.floor(h / 24)} d ${Math.round(h % 24)} h` : `${Math.round(h)} h`;
}

/** The catch-up items in plan order, with what's next first. */
export function catchupQueue(e: Everything | undefined): { c: CatchupSummary; items: DashboardItem[] } | null {
  const c = e?.catchup;
  if (!e || !c?.active) return null;
  const byId = new Map(e.items.map((i) => [i.id, i]));
  return { c, items: c.order.map((id) => byId.get(id)).filter((i): i is DashboardItem => !!i) };
}

const isDone = (i: DashboardItem) => i.status !== 'open' || (i.minutesLeft != null && i.minutesLeft <= 0 && (i.estMinutes ?? 0) > 0);
const nextBooked = (i: DashboardItem, now: number) => i.booked.find((b) => ms(b.end) > now && b.status !== 'missed');

function startItem(i: DashboardItem) {
  if (i.guide || i.related.length) navigate('/notes', { guide: i.id });
  else navigate('/everything', { item: i.id });
}

/** One line at the top of the calendar and the Assistant: deadline, progress, and the session to do now. */
export function RedAlertBanner() {
  const e = useEverythingData();
  const now = useNow(60_000);
  const q = catchupQueue(e.data);
  if (!q) return null;
  const { c, items } = q;
  const until = ms(c.until);
  const next = items.find((i) => !isDone(i));
  const slot = next ? nextBooked(next, now) : null;
  const red = c.phase === 'red alert';
  return (
    <div className={`redalert-banner${red ? '' : ' after'}`}>
      <AlertTriangle size={18} />
      <div className="redalert-banner-text">
        <b>{red ? `Red alert · catch up by ${dayName(until)} ${time(until)}` : 'Catching up'}</b>
        <span>
          {c.done} of {c.total} done · {minutes(c.minutesLeft)} left{red ? ` · ${hoursLeft(until, now)} to go` : ''}
          {c.onTrack === false && c.projectedFinish ? ` · at this pace, done ${dayName(ms(c.projectedFinish))}` : ''}
        </span>
        {next && (
          <span className="redalert-next">
            Next: {next.title}
            {slot ? ` · ${dayName(ms(slot.start))} ${time(ms(slot.start))}` : ''}
          </span>
        )}
      </div>
      {next && (
        <button type="button" className="btn sm danger-solid" onClick={() => startItem(next)}>
          <Play size={14} /> Start
        </button>
      )}
      <a className="btn sm ghost" href="#/everything">
        Plan
      </a>
    </div>
  );
}

/** The whole plan, at the top of the Everything page. */
export function RedAlertPanel() {
  const e = useEverythingData();
  const qc = useQueryClient();
  const now = useNow(60_000);
  const [all, setAll] = useState(false);
  const q = catchupQueue(e.data);
  if (!q) return null;
  const { c, items } = q;
  const until = ms(c.until);
  const red = c.phase === 'red alert';
  const todo = items.filter((i) => !isDone(i));
  const shown = all ? items : [...items.filter(isDone).slice(-2), ...todo.slice(0, 12)];
  const pct = c.minutesTotal ? Math.round((100 * (c.minutesTotal - c.minutesLeft)) / c.minutesTotal) : 0;
  const tick = async (i: DashboardItem) => {
    try {
      await api.post('/everything/actions', { type: isDone(i) ? 'reopen' : 'done', itemId: i.id });
      await qc.invalidateQueries({ queryKey: ['everything'] });
      toast(isDone(i) ? 'Reopened' : `Done: ${i.title.slice(0, 50)}`);
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };
  return (
    <section className={`redalert-panel${red ? '' : ' after'}`}>
      <div className="redalert-head">
        <AlertTriangle size={22} />
        <div className="grow">
          <h2>{red ? 'Red alert: catch up on everything' : 'Catching up'}</h2>
          <div className="small">
            {red ? `Teaching resumes ${dayName(until)} ${time(until)} (${relative(until, now)}). ` : ''}
            {c.done} of {c.total} done · {minutes(c.minutesLeft)} of accelerated work left
            {c.projectedFinish ? ` · at this pace finished ${dayName(ms(c.projectedFinish))} ${time(ms(c.projectedFinish))}` : ''}
          </div>
        </div>
      </div>
      <div className="progress redalert-progress">
        <span style={{ width: `${pct}%` }} />
      </div>
      <p className="small redalert-how">
        Tempo books these in order (Monday's maths tests first, then every lecture, module by module) and asks after each
        session whether you did it. Not done = booked again. Each one opens a guide: the notes' key results, exam flags
        and self-test, so a lecture takes about 25 minutes instead of 50.
      </p>
      <ol className="redalert-list">
        {shown.map((i) => {
          const done = isDone(i);
          const slot = nextBooked(i, now);
          return (
            <li key={i.id} className={done ? 'done' : ''}>
              <button type="button" className="redalert-tick" aria-label={done ? 'Reopen' : 'Done'} onClick={() => void tick(i)}>
                {done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
              </button>
              <div className="grow">
                <div className="redalert-title">{i.title}</div>
                <div className="small faint">
                  {[
                    i.module,
                    i.estMinutes ? minutes(i.minutesLeft != null && i.minutesLeft > 0 ? i.minutesLeft : i.estMinutes) : null,
                    slot ? `booked ${dayName(ms(slot.start))} ${time(ms(slot.start))}` : done ? null : 'not booked yet',
                    slot && ms(slot.start) > until ? 'after the deadline' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              {!done && (
                <button type="button" className="btn sm" onClick={() => startItem(i)}>
                  <Play size={13} /> Start
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {items.length > shown.length || all ? (
        <button type="button" className="btn sm ghost" onClick={() => setAll((a) => !a)}>
          {all ? 'Show what’s next' : `Show all ${items.length}`}
        </button>
      ) : null}
    </section>
  );
}

/** "Did you do these?": every class and study session that ended without a done / not done. */
export function MarkPrompt() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['marking'], queryFn: () => api.get<{ items: MarkItem[] }>('/marking'), refetchInterval: 60_000 });
  const [busy, setBusy] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  const answer = async (m: MarkItem, done: boolean) => {
    setBusy(m.key);
    try {
      await api.post('/marking', { kind: m.kind, id: m.id, done });
      await Promise.all([qc.invalidateQueries({ queryKey: ['marking'] }), qc.invalidateQueries({ queryKey: ['plan'] }), qc.invalidateQueries({ queryKey: ['everything'] })]);
      toast(done ? 'Marked done' : m.kind === 'class' ? 'Not done: School Manager will add a catch-up session' : 'Not done: booked again');
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="mark-prompt" aria-live="polite">
      <div className="mark-prompt-head">
        <AlertTriangle size={16} /> <b>Did you do {items.length === 1 ? 'this' : `these ${items.length}`}?</b>
        <span className="small">Anything not done gets caught up.</span>
      </div>
      <ul>
        {items.slice(0, 8).map((m) => (
          <li key={m.key}>
            <div className="grow">
              <div className="mark-title">{m.title}</div>
              <div className="small faint">
                {[m.kind === 'class' ? m.session || 'class' : 'study session', `${dayName(m.start)} ${time(m.start)}–${time(m.end)}`].join(' · ')}
              </div>
            </div>
            <button type="button" className="btn sm primary" disabled={busy === m.key} onClick={() => void answer(m, true)}>
              <Check size={14} /> {m.kind === 'class' ? 'Went' : 'Done'}
            </button>
            <button type="button" className="btn sm" disabled={busy === m.key} onClick={() => void answer(m, false)}>
              <X size={14} /> {m.kind === 'class' ? "Didn't go" : 'Not done'}
            </button>
          </li>
        ))}
      </ul>
      {items.length > 8 && <div className="small faint">…and {items.length - 8} more.</div>}
    </section>
  );
}
